import React, { useState } from 'react';

const AnimatedEmojis = ({ emojis = ['🐂', '🐃', '🐏', '🐑', '🐐', '🐎', '🫏', '🐔', '🐓', '🐣', '🦆', '🦃', '🦙'], spacing = 60, speed = 40 }) => {
    const [runningAway, setRunningAway] = useState(new Map()); // Map to store direction for each emoji

    // Different background colors for each emoji
    const backgroundColors = [
        'linear-gradient(135deg, #ff6b6b, #ee5a6f)',
        'linear-gradient(135deg, #4ecdc4, #44a08d)',
        'linear-gradient(135deg, #ffe66d, #ffd93d)',
        'linear-gradient(135deg, #a8e6cf, #88d8a3)',
        'linear-gradient(135deg, #ffd3a5, #fd9853)',
        'linear-gradient(135deg, #a8caba, #5d4e75)',
        'linear-gradient(135deg, #ffecd2, #fcb69f)',
        'linear-gradient(135deg, #667eea, #764ba2)',
        'linear-gradient(135deg, #f093fb, #f5576c)',
        'linear-gradient(135deg, #4facfe, #00f2fe)',
        'linear-gradient(135deg, #43e97b, #38f9d7)',
        'linear-gradient(135deg, #fa709a, #fee140)',
        'linear-gradient(135deg, #30cfd0, #330867)'
    ];

    const getRandomDirection = () => {
        const directions = [
            { x: 500, y: -200, rotate: 720 },      // Top-right
            { x: -500, y: -200, rotate: -720 },   // Top-left
            { x: 500, y: 200, rotate: 720 },      // Bottom-right
            { x: -500, y: 200, rotate: -720 },     // Bottom-left
            { x: 0, y: -400, rotate: 720 },        // Straight up
            { x: 0, y: 400, rotate: -720 },         // Straight down
            { x: 500, y: 0, rotate: 720 },         // Straight right
            { x: -500, y: 0, rotate: -720 },       // Straight left
            { x: 400, y: -300, rotate: 1080 },     // Diagonal top-right
            { x: -400, y: -300, rotate: -1080 },   // Diagonal top-left
            { x: 400, y: 300, rotate: 1080 },      // Diagonal bottom-right
            { x: -400, y: 300, rotate: -1080 },   // Diagonal bottom-left
        ];
        return directions[Math.floor(Math.random() * directions.length)];
    };

    const handleEmojiClick = (key) => {
        // Generate random direction for this emoji
        const direction = getRandomDirection();
        
        // Add to running away map with direction
        setRunningAway(prev => {
            const newMap = new Map(prev);
            newMap.set(key, direction);
            return newMap;
        });

        // Reset after animation completes (2 seconds)
        setTimeout(() => {
            setRunningAway(prev => {
                const newMap = new Map(prev);
                newMap.delete(key);
                return newMap;
            });
        }, 2000);
    };

    return (
        <div className="animated-emojis-container" style={{ 
            marginTop: 24,
            marginBottom: 24,
            overflow: 'visible',
            width: '100%',
            position: 'relative',
            padding: '20px 0'
        }}>
            <div className="animated-emojis-line" style={{ gap: `${spacing}px` }}>
                {emojis.map((emoji, index) => {
                    const firstKey = `first-${index}`;
                    const direction = runningAway.get(firstKey);
                    const isRunningAway = !!direction;
                    const bgColor = backgroundColors[index % backgroundColors.length];
                    return (
                        <span 
                            key={firstKey} 
                            className={`emoji-wrapper ${isRunningAway ? 'emoji-wrapper-run-away' : ''}`}
                            style={{ 
                                '--emoji-bg': bgColor,
                                ...(isRunningAway ? {
                                    '--run-away-x': `${direction.x}px`,
                                    '--run-away-y': `${direction.y}px`,
                                    '--run-away-rotate': `${direction.rotate}deg`
                                } : {})
                            }}
                        >
                            <span 
                                className={`emoji-item ${isRunningAway ? 'emoji-run-away' : ''}`} 
                                style={{ 
                                    animationDelay: `${index * 0.15}s`
                                }}
                                onClick={() => handleEmojiClick(firstKey)}
                            >
                                {emoji}
                            </span>
                        </span>
                    );
                })}
                {/* Duplicate for seamless loop */}
                {emojis.map((emoji, index) => {
                    const secondKey = `second-${index}`;
                    const direction = runningAway.get(secondKey);
                    const isRunningAway = !!direction;
                    const bgColor = backgroundColors[index % backgroundColors.length];
                    return (
                        <span 
                            key={secondKey} 
                            className={`emoji-wrapper ${isRunningAway ? 'emoji-wrapper-run-away' : ''}`}
                            style={{ 
                                '--emoji-bg': bgColor,
                                ...(isRunningAway ? {
                                    '--run-away-x': `${direction.x}px`,
                                    '--run-away-y': `${direction.y}px`,
                                    '--run-away-rotate': `${direction.rotate}deg`
                                } : {})
                            }}
                        >
                            <span 
                                className={`emoji-item ${isRunningAway ? 'emoji-run-away' : ''}`} 
                                style={{ 
                                    animationDelay: `${index * 0.15}s`
                                }}
                                onClick={() => handleEmojiClick(secondKey)}
                            >
                                {emoji}
                            </span>
                        </span>
                    );
                })}
            </div>
            
            <style jsx>{`
                @keyframes emoji-bounce {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50% { transform: translateY(-10px) scale(1.1); }
                }
                
                @keyframes emoji-scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                
                .animated-emojis-container {
                    min-height: 100px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: visible;
                }
                
                .animated-emojis-line {
                    display: flex;
                    align-items: center;
                    white-space: nowrap;
                    animation: emoji-scroll ${speed}s linear infinite;
                    width: fit-content;
                    overflow: visible;
                }
                
                .emoji-wrapper {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 60px;
                    height: 60px;
                    overflow: visible;
                }
                
                .emoji-wrapper::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: var(--emoji-bg);
                    opacity: 0.8;
                    z-index: 0;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;
                }
                
                .emoji-wrapper:hover::before {
                    transform: translate(-50%, -50%) scale(1.2);
                    opacity: 1;
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
                }
                
                .emoji-item {
                    font-size: 40px;
                    display: inline-block;
                    animation: emoji-bounce 2s ease-in-out infinite;
                    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
                    cursor: pointer;
                    transition: transform 0.3s ease;
                    position: relative;
                    z-index: 1;
                }
                
                .emoji-item:hover {
                    transform: scale(1.3) rotate(10deg);
                }
                
                @keyframes emoji-run-away {
                    0% {
                        transform: translateX(0) translateY(0) rotate(0deg) scale(1);
                        opacity: 1;
                    }
                    25% {
                        transform: translateX(calc(var(--run-away-x) * 0.2)) translateY(calc(var(--run-away-y) * 0.25)) rotate(calc(var(--run-away-rotate) * 0.25)) scale(1.2);
                        opacity: 0.8;
                    }
                    50% {
                        transform: translateX(calc(var(--run-away-x) * 0.4)) translateY(calc(var(--run-away-y) * 0.5)) rotate(calc(var(--run-away-rotate) * 0.5)) scale(1.4);
                        opacity: 0.6;
                    }
                    75% {
                        transform: translateX(calc(var(--run-away-x) * 0.6)) translateY(calc(var(--run-away-y) * 0.75)) rotate(calc(var(--run-away-rotate) * 0.75)) scale(1.6);
                        opacity: 0.4;
                    }
                    100% {
                        transform: translateX(var(--run-away-x)) translateY(var(--run-away-y)) rotate(var(--run-away-rotate)) scale(2);
                        opacity: 0;
                    }
                }
                
                .emoji-run-away {
                    animation: emoji-run-away 2s ease-out forwards !important;
                    z-index: 1000;
                    pointer-events: none;
                }
                
                .emoji-wrapper-run-away {
                    animation: emoji-wrapper-run-away 2s ease-out forwards !important;
                    z-index: 1000;
                    pointer-events: none;
                }
                
                .emoji-wrapper-run-away::before {
                    animation: emoji-bg-fade 2s ease-out forwards;
                }
                
                @keyframes emoji-wrapper-run-away {
                    0% {
                        transform: translateX(0) translateY(0) rotate(0deg);
                    }
                    100% {
                        transform: translateX(var(--run-away-x)) translateY(var(--run-away-y)) rotate(var(--run-away-rotate));
                    }
                }
                
                @keyframes emoji-bg-fade {
                    0% {
                        opacity: 0.8;
                        transform: scale(1);
                    }
                    100% {
                        opacity: 0;
                        transform: scale(0.5);
                    }
                }
                
                @media (max-width: 768px) {
                    .emoji-item {
                        font-size: 32px;
                    }
                    .animated-emojis-container {
                        height: 50px;
                    }
                }
                
                @media (max-width: 480px) {
                    .emoji-item {
                        font-size: 28px;
                    }
                    .animated-emojis-container {
                        height: 45px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AnimatedEmojis;

